import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Warning, Plus, Leaf, Egg, Carrot, Drop, Orange, ListChecks, Camera, FileText } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { Md } from '@/components/Md';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const GROUP_ICONS = { energia: Leaf, ferro_proteina: Egg, horticolas: Carrot, gordura: Drop, micronutrientes: Orange };
const REACTIONS = [
  { value: 'sem_reacao', label: 'Sem reação', color: 'text-green-600 bg-green-50' },
  { value: 'reacao_ligeira', label: 'Reação ligeira', color: 'text-amber-600 bg-amber-50' },
  { value: 'reacao_adversa', label: 'Reação adversa', color: 'text-red-600 bg-red-50' },
];
const NUTRI_REACTIONS = {
  boa_escolha: { emoji: '👍', label: 'Boa escolha' },
  vamos_ajustar: { emoji: '⚠️', label: 'Vamos ajustar' },
  adorei: { emoji: '❤️', label: 'Adorei' },
};

export default function ChecklistIntroPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const [introductions, setIntroductions] = useState([]);
  const [babyFoods, setBabyFoods] = useState([]);
  const [groups, setGroups] = useState([]);
  const [threeDayCheck, setThreeDayCheck] = useState({});
  const [candidateCheck, setCandidateCheck] = useState(null);
  const [showAdd, setShowAdd] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [reaction, setReaction] = useState('sem_reacao');
  const [note, setNote] = useState('');
  const [introDate, setIntroDate] = useState(new Date().toISOString().split('T')[0]);
  const [quantity, setQuantity] = useState('');
  const [method, setMethod] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const [iRes, fRes, tdRes, gRes] = await Promise.all([
        axios.get(`${API}/api/my-food-introductions`),
        axios.get(`${API}/api/baby-foods`),
        axios.get(`${API}/api/my-food-introductions/three-day-check`),
        axios.get(`${API}/api/baby-food-groups`),
      ]);
      setIntroductions(iRes.data);
      setBabyFoods(fRes.data);
      setThreeDayCheck(tdRes.data);
      setGroups(gRes.data || []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const introducedNames = new Set(introductions.map(i => i.food_name));
  const introducedCount = introducedNames.size;
  const totalFoods = new Set(babyFoods.map(f => f.name)).size;
  const toleratedCategories = new Set(threeDayCheck.tolerated_categories || []);

  const handleIntroduce = async (food) => {
    setShowAdd(food);
    setReaction('sem_reacao');
    setNote('');
    setPhotoFile(null);
    setIntroDate(new Date().toISOString().split('T')[0]);
    setCandidateCheck(null);
    try {
      const { data } = await axios.get(`${API}/api/my-food-introductions/three-day-check`, { params: { food_name: food.name } });
      setCandidateCheck(data);
    } catch {}
  };

  const submitIntroduction = async () => {
    try {
      const { data } = await axios.post(`${API}/api/my-food-introductions`, {
        food_name: showAdd.name, group: showAdd.group, reaction, note, introduced_date: introDate, quantity, method,
      });
      if (photoFile && data?.id) {
        try { const fd = new FormData(); fd.append('file', photoFile); await axios.post(`${API}/api/my-food-introductions/${data.id}/photo`, fd); } catch { toast.error('Registo guardado, mas a foto falhou'); }
      }
      toast.success(`${showAdd.name} registado!`);
      setShowAdd(null); setQuantity(''); setMethod(''); setPhotoFile(null);
      fetch();
    } catch { toast.error('Erro ao registar'); }
  };

  const getReactionInfo = (val) => REACTIONS.find(r => r.value === val) || REACTIONS[0];

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="checklist-intro-page">
      <div className="mb-5">
        <h2 className="font-heading font-medium text-xl text-brand-text">Checklist de Introdução</h2>
        <p className="text-xs text-brand-muted mt-1">Regista cada alimento novo que o bebé experimenta</p>
      </div>

      {/* Progress */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="intro-progress">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-brand-muted">Progresso de introdução</span>
          <span className="text-xs font-medium text-brand-green">{introducedCount}/{totalFoods} alimentos</span>
        </div>
        <div className="w-full bg-brand-bg rounded-full h-2">
          <div className="bg-brand-green h-2 rounded-full transition-all" style={{ width: `${totalFoods > 0 ? (introducedCount / totalFoods) * 100 : 0}%` }} />
        </div>
      </div>

      {/* 3-day warning */}
      {threeDayCheck && !threeDayCheck.can_introduce && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl mb-4 text-sm text-amber-700" data-testid="three-day-warning">
          <Warning size={18} weight="fill" />
          <span>Regra dos 3 dias (alergéneo): espera mais {3 - (threeDayCheck.days_since || 0)} dia(s). Último alergéneo: <strong>{threeDayCheck.last_food}</strong> ({threeDayCheck.last_date})</span>
        </div>
      )}

      {/* Diversity alert */}
      {(() => {
        if (introductions.length === 0) return null;
        const dates = introductions.map(i => i.introduced_date).filter(Boolean).sort();
        const lastDate = dates[dates.length - 1];
        if (!lastDate) return null;
        const daysSince = Math.floor((Date.now() - new Date(lastDate).getTime()) / 86400000);
        if (daysSince > 7) return (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4 flex items-center gap-2">
            <Warning size={16} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800">Não foram introduzidos alimentos novos há {daysSince} dias. A diversidade é importante!</p>
          </div>
        );
        return null;
      })()}
      {/* Foods by group — dynamic from API */}
      <div className="space-y-4">
        {groups.map(g => {
          const group = g.key;
          const foods = babyFoods.filter(f => f.group === group);
          const uniqueFoods = [...new Map(foods.map(f => [f.name, f])).values()];
          if (uniqueFoods.length === 0) return null;
          const Icon = GROUP_ICONS[group] || Leaf;
          // Semáforo: nenhum / em introdução / consolidado
          const introInGroup = uniqueFoods.filter(f => introducedNames.has(f.name)).length;
          const semaforo = introInGroup === 0 ? 'nenhum' : introInGroup >= uniqueFoods.length ? 'consolidado' : 'em_introducao';
          const semaforoColors = { nenhum: 'bg-gray-200', em_introducao: 'bg-amber-400', consolidado: 'bg-brand-green' };
          const semaforoLabels = { nenhum: 'Nenhum', em_introducao: 'Em introdução', consolidado: 'Consolidado' };
          return (
            <div key={group} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`checklist-group-${group}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-brand-green" weight="duotone" />
                <h3 className="font-heading font-medium text-sm text-brand-text">{g.label}</h3>
                <span className="text-[9px] text-brand-muted">{introInGroup}/{uniqueFoods.length}</span>
                <span className={`w-2.5 h-2.5 rounded-full ${semaforoColors[semaforo]}`} title={semaforoLabels[semaforo]} />
              </div>
              <div className="space-y-1.5">
                {uniqueFoods.map(food => {
                  const intro = introductions.find(i => i.food_name === food.name);
                  const isIntroduced = !!intro;
                  const reactionInfo = intro ? getReactionInfo(intro.reaction) : null;
                  return (
                    <div key={food.name} className="flex items-center gap-3 py-1.5 cursor-pointer" data-testid={`food-item-${food.name}`} onClick={() => setShowDetail(food)}>
                      {isIntroduced ? (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${reactionInfo?.color || 'bg-green-50 text-green-600'}`}>
                          <Check size={12} weight="bold" />
                        </div>
                      ) : (
                        <button onClick={() => handleIntroduce(food)} className="w-6 h-6 rounded-full border-2 border-brand-border flex items-center justify-center flex-shrink-0 hover:border-brand-green hover:bg-brand-green/5 transition-colors" data-testid={`introduce-btn-${food.name}`}>
                          <Plus size={10} className="text-brand-muted" />
                        </button>
                      )}
                      <span className={`text-sm flex-1 ${isIntroduced ? 'text-brand-text' : 'text-brand-muted'}`}>{food.name}</span>
                      {food.is_allergen && <span className="text-[8px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0">Alergénio</span>}
                      {food.is_allergen && (food.allergen_categories?.length ? food.allergen_categories : [food.allergen_category]).filter(Boolean).length > 0 && (food.allergen_categories?.length ? food.allergen_categories : [food.allergen_category]).filter(Boolean).every(c => toleratedCategories.has(c)) && (
                        <span className="text-[8px] bg-brand-green/10 text-brand-green border border-brand-green/30 px-1.5 py-0.5 rounded-full flex-shrink-0" data-testid={`group-tolerated-badge-${food.name}`} title={`Grupo "${food.allergen_category}" já tolerado, sem restrição da regra dos 3 dias`}>
                          Grupo já tolerado ✓
                        </span>
                      )}
                      {isIntroduced && (
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[9px] px-2 py-0.5 rounded-full ${reactionInfo?.color}`}>{reactionInfo?.label}</span>
                          <span className="text-[9px] text-brand-placeholder">{intro.introduced_date}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add introduction dialog */}
      <Dialog open={!!showAdd} onOpenChange={(v) => { if (!v) setShowAdd(null); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar introdução</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-brand-text">A registar: <strong>{showAdd?.name}</strong></p>
            <div>
              <p className="text-xs text-brand-muted mb-1">Data de introdução *</p>
              <Input type="date" value={introDate} onChange={e => setIntroDate(e.target.value)} className="rounded-xl border-brand-border" max={new Date().toISOString().split('T')[0]} data-testid="intro-date" />
            </div>
            {/* 3-day warning — só se o GRUPO deste alimento ainda não estiver tolerado (ex.: Couscous
                depois de Massa não mostra aviso, mas Sardinha combinada com outro alergénio novo mostra) */}
            {candidateCheck && candidateCheck.candidate_is_new_allergen && candidateCheck.is_allergen && showAdd && (() => {
              const lastDate = candidateCheck.last_date;
              if (!lastDate) return null;
              const daysSince = Math.floor((new Date(introDate) - new Date(lastDate)) / 86400000);
              if (daysSince < 3) return (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-700" data-testid="candidate-allergen-warning">
                  <span>Regra dos 3 dias: faltam {3 - daysSince} dia(s) desde o último alergénio de grupo novo (<strong>{candidateCheck.last_food}</strong>, {lastDate}). Podes registar na mesma se a introdução real foi antes.</span>
                </div>
              );
              return null;
            })()}
            <div>
              <p className="text-xs text-brand-muted mb-2">Reação do bebé:</p>
              <div className="space-y-1.5">
                {REACTIONS.map(r => (
                  <button key={r.value} onClick={() => setReaction(r.value)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm border transition-all ${reaction === r.value ? 'border-brand-green bg-brand-green/5 font-medium' : 'border-brand-border'}`}
                    data-testid={`reaction-${r.value}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Nota (opcional)</p>
              <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Observações..." className="rounded-xl border-brand-border text-sm resize-none" rows={2} />
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Quantidade (opcional)</p>
              <div className="flex gap-1.5">
                {['Primeira vez (mínima)', 'Normal'].map(q => (
                  <button key={q} type="button" onClick={() => setQuantity(quantity === q ? '' : q)} className={`flex-1 px-2 py-1.5 rounded-xl text-[10px] font-medium ${quantity === q ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{q}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Método (opcional)</p>
              <div className="flex gap-1.5 flex-wrap">
                {['Colher', 'BLW', 'Misto'].map(m => (
                  <button key={m} type="button" onClick={() => setMethod(method === m ? '' : m)} className={`px-3 py-1.5 rounded-full text-[10px] font-medium ${method === m ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{m}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs text-brand-muted mb-1">Foto (opcional)</p>
              {photoFile ? (
                <div className="flex items-center gap-2">
                  <img src={URL.createObjectURL(photoFile)} alt="pré-visualização" className="w-16 h-16 rounded-xl object-cover border border-brand-border" />
                  <button type="button" onClick={() => setPhotoFile(null)} className="text-xs text-red-500" data-testid="remove-intro-photo">Remover</button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 border border-dashed border-brand-border rounded-xl py-3 text-xs text-brand-muted cursor-pointer hover:border-brand-green" data-testid="intro-photo-label">
                  <Camera size={16} /> Adicionar foto da refeição
                  <input type="file" accept="image/*" capture="environment" onChange={e => setPhotoFile(e.target.files?.[0] || null)} className="hidden" data-testid="intro-photo-input" />
                </label>
              )}
            </div>
            <button onClick={submitIntroduction} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="submit-intro-btn">Registar</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Food detail modal */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-brand-text">{showDetail.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {showDetail.image_path && <img src={`${API}/api/files/${showDetail.image_path}`} alt={showDetail.name} className="w-full h-40 object-cover rounded-xl" />}
                {showDetail.description && <div><p className="text-[10px] text-brand-muted uppercase mb-0.5">Notas clínicas</p><p className="text-xs text-brand-text">{showDetail.description}</p></div>}
                {showDetail.texture && typeof showDetail.texture === 'object' && (() => {
                  // Determine baby's current phase based on age
                  const bd = user?.baby_birth_date || user?.birth_date || '';
                  let phase = 'fase1';
                  if (bd) { const ageMonths = Math.floor((Date.now() - new Date(bd).getTime()) / (30.44*24*60*60*1000)); if (ageMonths >= 12) phase = 'fase3'; else if (ageMonths >= 9) phase = 'fase2'; }
                  const phaseTextures = showDetail.texture[phase] || [];
                  const phaseLabels = { fase1: 'Fase 1 (6-9m)', fase2: 'Fase 2 (9-12m)', fase3: 'Fase 3 (12-24m)' };
                  return phaseTextures.length > 0 ? (
                    <div><p className="text-[10px] text-brand-muted uppercase mb-0.5">Textura recomendada ({phaseLabels[phase]})</p><div className="flex gap-1">{phaseTextures.map(t => <span key={t} className="text-xs bg-brand-green/10 text-brand-green px-2 py-0.5 rounded-full">{t}</span>)}</div></div>
                  ) : null;
                })()}
                {showDetail.texture && typeof showDetail.texture === 'string' && <div><p className="text-[10px] text-brand-muted uppercase mb-0.5">Textura recomendada</p><p className="text-xs text-brand-text">{showDetail.texture}</p></div>}
                {showDetail.preparation_tips && <div><p className="text-[10px] text-brand-muted uppercase mb-0.5">Preparação e corte</p><p className="text-xs text-brand-text whitespace-pre-line">{showDetail.preparation_tips}</p></div>}
                {showDetail.portions && typeof showDetail.portions === 'object' && Object.keys(showDetail.portions).length > 0 && (() => {
                  const bd = user?.baby_birth_date || user?.birth_date || '';
                  let phase = 'fase1';
                  if (bd) { const ageMonths = Math.floor((Date.now() - new Date(bd).getTime()) / (30.44*24*60*60*1000)); if (ageMonths >= 12) phase = 'fase3'; else if (ageMonths >= 9) phase = 'fase2'; }
                  const currentPortion = showDetail.portions[phase];
                  const phaseLabels = { fase1: 'Fase 1 (6-9m)', fase2: 'Fase 2 (9-12m)', fase3: 'Fase 3 (12-24m)' };
                  return currentPortion ? (
                    <div className="bg-brand-bg/30 rounded-lg p-2">
                      <p className="text-[9px] text-brand-muted">Porção recomendada ({phaseLabels[phase]})</p>
                      <p className="text-xs font-medium text-brand-text">{currentPortion}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[10px] text-brand-muted uppercase mb-1">Porção por fase</p>
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(showDetail.portions).map(([faixa, porcao]) => porcao ? (
                          <div key={faixa} className="bg-brand-bg/30 rounded-lg p-2 text-center">
                            <p className="text-[9px] text-brand-muted">{phaseLabels[faixa] || faixa}</p>
                            <p className="text-xs font-medium text-brand-text">{porcao}</p>
                          </div>
                        ) : null)}
                      </div>
                    </div>
                  );
                })()}
                {(showDetail.min_age || showDetail.months) && (
                  <p className="text-[10px] text-brand-muted">
                    A partir dos {typeof showDetail.min_age === 'number' ? showDetail.min_age :
                                  typeof showDetail.months === 'number' ? showDetail.months :
                                  Array.isArray(showDetail.months) && showDetail.months.length > 0 ? Math.min(...showDetail.months) :
                                  ''} meses
                  </p>
                )}
                {showDetail.is_allergen && <span className="inline-block text-[9px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-medium">Alergénio</span>}
                {/* Articles/Resources */}
                {Array.isArray(showDetail.articles) && showDetail.articles.length > 0 && (
                  <div data-testid="food-detail-articles">
                    <p className="text-[10px] text-brand-muted uppercase mb-1">Recursos</p>
                    {(() => {
                      const images = showDetail.articles.filter(a => a.type === 'image');
                      return images.length > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
                          {images.map((a, i) => (
                            <img key={i} data-testid={`food-article-image-${i}`} src={a.url || `${API}/api/files/${a.path}`} alt="" className="w-full max-w-[280px] h-auto rounded-xl flex-shrink-0 snap-center cursor-pointer" onClick={() => window.open(a.url || `${API}/api/files/${a.path}`, '_blank')} />
                          ))}
                        </div>
                      ) : null;
                    })()}
                    {showDetail.articles.filter(a => a.type === 'text').map((a, i) => (
                      <Md key={i} as="p" data-testid={`food-article-text-${i}`} className="text-xs text-brand-text mt-1">{a.content}</Md>
                    ))}
                    {showDetail.articles.filter(a => a.type === 'file').map((a, i) => (
                      <a key={i} data-testid={`food-article-file-${i}`} href={`${API}/api/files/${a.path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 mt-1 text-xs text-brand-green hover:underline">
                        <FileText size={12} /> {a.name || 'Ver ficheiro'}
                      </a>
                    ))}
                    {showDetail.articles.filter(a => a.type === 'video').map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block mt-1 text-xs text-brand-green hover:underline">{a.title || 'Ver vídeo'}</a>
                    ))}
                    {showDetail.articles.filter(a => a.type === 'link').map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block mt-1 text-xs text-brand-green hover:underline">{a.title || a.url}</a>
                    ))}
                  </div>
                )}
                {/* Introduction status + exposures */}
                {(() => { const intro = introductions.find(i => i.food_name === showDetail.name); if (!intro) return <p className="text-xs text-brand-placeholder italic">Ainda não introduzido</p>; return (
                  <div className="bg-brand-green/5 border border-brand-green/20 rounded-xl p-3 space-y-1" data-testid={`intro-detail-${intro.id}`}>
                    <p className="text-xs text-brand-green font-medium">Introduzido a {intro.introduced_date}</p>
                    <p className="text-[10px] text-brand-muted">{getReactionInfo(intro.reaction)?.label || intro.reaction}{intro.note ? `: ${intro.note}` : ''}</p>
                    {intro.method && <p className="text-[10px] text-brand-muted">Método: {intro.method}</p>}
                    {intro.quantity && <p className="text-[10px] text-brand-muted">Quantidade: {intro.quantity}</p>}
                    {/* Foto do registo */}
                    {intro.photo_path ? (
                      <div className="pt-1">
                        <img src={`${API}/api/files/${intro.photo_path}`} alt="Foto da refeição" className="w-full max-h-48 object-cover rounded-xl cursor-pointer" data-testid="intro-photo-view" onClick={() => window.open(`${API}/api/files/${intro.photo_path}`, '_blank')} />
                        <button onClick={async () => { const ok = await confirmDelete({ description: `Vai apagar a foto da introdução de ${showDetail.name}.` }); if (!ok) return; try { await axios.delete(`${API}/api/my-food-introductions/${intro.id}/photo`); toast.success('Foto removida'); fetch(); } catch { toast.error('Erro'); } }} className="text-[9px] text-red-500 mt-1">Remover foto</button>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-1 text-[10px] text-brand-green cursor-pointer pt-1" data-testid="intro-detail-add-photo">
                        <Camera size={12} /> Adicionar foto
                        <input type="file" accept="image/*" capture="environment" onChange={async e => { const f = e.target.files?.[0]; if (!f) return; try { const fd = new FormData(); fd.append('file', f); await axios.post(`${API}/api/my-food-introductions/${intro.id}/photo`, fd); toast.success('Foto adicionada'); fetch(); } catch { toast.error('Erro ao enviar foto'); } }} className="hidden" />
                      </label>
                    )}
                    {/* Feedback da nutricionista */}
                    {(intro.nutri_reaction || intro.nutri_comment) && (
                      <div className="mt-2 pt-2 border-t border-brand-green/10 bg-brand-gold/5 -mx-3 -mb-3 px-3 pb-3 rounded-b-xl" data-testid="intro-nutri-feedback">
                        <p className="text-[9px] text-brand-gold-hover uppercase tracking-wider font-medium mb-1">Resposta da nutricionista</p>
                        {intro.nutri_reaction && NUTRI_REACTIONS[intro.nutri_reaction] && (
                          <p className="text-xs text-brand-text">{NUTRI_REACTIONS[intro.nutri_reaction].emoji} {NUTRI_REACTIONS[intro.nutri_reaction].label}</p>
                        )}
                        {intro.nutri_comment && <p className="text-[11px] text-brand-text mt-0.5 whitespace-pre-line">{intro.nutri_comment}</p>}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-1 pt-1 border-t border-brand-green/10">
                      <span className="text-[10px] text-brand-muted">{intro.exposures || 1} de 10 exposições</span>
                      <div className="flex-1 h-1.5 bg-brand-bg rounded-full overflow-hidden">
                        <div className="h-full bg-brand-green rounded-full" style={{ width: `${Math.min(100, ((intro.exposures || 1) / 10) * 100)}%` }} />
                      </div>
                      <button onClick={async () => { await axios.put(`${API}/api/my-food-introductions/${intro.id}/exposure`); toast.success('Exposição registada'); fetch(); }} className="text-[9px] text-brand-green bg-brand-green/10 px-2 py-0.5 rounded-full hover:bg-brand-green/20">+1</button>
                    </div>
                  </div>
                ); })()}
                {!introducedNames.has(showDetail.name) && (
                  <button onClick={() => { setShowDetail(null); handleIntroduce(showDetail); }} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium">Registar introdução</button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
